import React from 'react';

import Group from '@lightspeed/cirrus-group';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '@lightspeed/cirrus-modal';
import { Button } from '@lightspeed/flame/Button';
import { Divider } from '@lightspeed/flame/Divider';
import { IconChevronLeft } from '@lightspeed/flame/Icon/ChevronLeft';
import Dropzone from 'dropzone';

import UploadErrors from './components/ImageUploadErrors';

const initialState = {
  showModal: false,
  imageCount: 0,
  imageLimit: 0,
  skippedImages: 0,
  skippedImageMessage: '',
  highlight: false,
  jobID: null,
  expectedImageMessage: null,
  imagesOverLimit: [],
  showProgressBar: false,
  totalImageCount: 0,
  cloudinaryUploadPreset: '',
  uploadedImageCount: 0,
  uploadedImagePercentage: 0,
  uploadComplete: false,
  imageOverLimitCount: 0,
  itemImportID: 0,
  modalTitle: 'Upload Images',
  showErrors: false,
  uploadErrors: {},
  remainingImages: 0,
  expectedFilenames: {},
};

export default class ImageUploadModal extends React.Component {
  constructor() {
    super();

    this.state = initialState;

    this.close = this.close.bind(this);
    this.toggleErrorScreen = this.toggleErrorScreen.bind(this);

    this.onModalOpen = this.onModalOpen.bind(this);
  }

  componentDidMount() {
    const { getRef } = this.props;
    if (getRef) getRef(this);
    window.merchantos.session.getImageUsage().then(() => {
      const imageUsage = window.merchantos.session.imageUsage;

      // eslint-disable-next-line eqeqeq
      if (imageUsage.Usage.imageCountSoftLimitReached == 'true') {
        // modifying the state
        const imageCount = imageUsage.Usage.imageCount;
        const imageLimit = imageUsage.Usage.defaultMaxImageCount;

        this.setState({
          imageCount,
          imageLimit,
        });
      }
    });
  }

  componentWillUnmount() {
    this.dropzone.destroy();
  }

  onModalOpen() {
    let dragStatus = 0;
    const self = this;
    const uploadURL = `https://api.cloudinary.com/v1_1/${$.cloudinary.config().cloud_name}/upload`;

    // eslint-disable-next-line no-undef
    this.dropzone = new Dropzone('#image-import-dropzone', {
      url: uploadURL,
      headers: { Accept: 'application/xml' },
      autoProcessQueue: true,
      acceptedFiles: 'image/jpeg, image/gif, image/png, .jpg, .jpeg, .png, .gif',
      clickable: ['#browse-images'],
      maxFilesize: 8,
      error: (file, errorMessage) => {
        self.skipImage();
        dragStatus = 0;

        // eslint-disable-next-line eqeqeq
        if (errorMessage == "You can't upload files of this type.") {
          // eslint-disable-next-line no-param-reassign
          errorMessage = 'Filetype not supported. Images can be of jpg, png, or gif formats.';
        }

        if (errorMessage.includes('File is too big')) {
          // eslint-disable-next-line no-param-reassign
          errorMessage = 'File exceeds 8MB.';
        }

        this.setState((prevState) => ({
          uploadErrors: Object.assign({}, prevState.uploadErrors, {
            [file.name]: errorMessage,
          }),
        }));

        if (typeof errorMessage === 'object') {
          if (errorMessage.message.indexOf('Item may not have more than 12 images') >= 0) {
            self.setState({ imageOverLimitCount: self.state.imageOverLimitCount + 1 });

            self.updateUploadProgress();

            // Enables done button if upload is complete
            self.checkUploadComplete();
          }
        }
      },
      accept: (file, done) => {
        const filename = file.name.toLowerCase();
        const fileMatch = self.checkImageMatch(filename);

        if (filename.length > 250) {
          done('File name exceeds 250 characters.');
        }

        if (fileMatch) {
          // Duplicate Image Upload
          if (self.state.expectedFilenames[fileMatch]) {
            done('File with same name already uploaded');
          } else {
            // Valid Image Upload
            const expectedFilenames = self.state.expectedFilenames;
            expectedFilenames[fileMatch] = true;

            self.setState(
              {
                expectedFilenames,
                remainingImages: self.state.remainingImages - 1,
              },
              () => {
                $('#images-expected').htmlReplace(self.state.remainingImages);
              }
            );

            done();
          }
        } else {
          // Invalid Image Upload
          done("File doesn't match any of the expected images.");
        }
      },
      sending: (file, xhr, formData) => {
        // Enables done button if upload is complete
        self.checkUploadComplete();

        self.setState({
          highlight: false,
          showProgressBar: true,
        });

        self.updateUploadProgress();

        dragStatus = 0;

        formData.append('timestamp', (Date.now() / 1000) | 0);
        formData.append('upload_preset', self.state.cloudinaryUploadPreset);
      },
      success: (file, response) => {
        const callbackURL =
          `https://${window.location.host}/` +
          'register_image.php?' +
          `cust_customer_id=${window.merchantos.session.getSystemCustomerID()}` +
          `&item_import_id=${self.state.itemImportID}&bytes=${response.bytes}` +
          `&created_at=${response.created_at}` +
          `&etag=${response.etag}` +
          `&format=${response.format}` +
          `&height=${response.height}` +
          `&public_id=${response.public_id}` +
          `&resource_type=${response.resource_type}` +
          `&signature=${response.signature}` +
          `&type=${response.type}` +
          `&version=${response.version}` +
          `&width=${response.width}`;

        $.when(
          window.lsCloud.startServerJob(
            {
              url: callbackURL,
            },
            false
          )
        )
          .done(() => {
            self.setState({ uploadedImageCount: self.state.uploadedImageCount + 1 });

            self.updateUploadProgress();
            self.checkUploadComplete();
          })
          .fail((data) => {
            self.setState({ imageOverLimitCount: self.state.imageOverLimitCount + 1 });
            self.skipImage();
            self.updateUploadProgress();

            self.setState({
              uploadErrors: Object.assign({}, self.state.uploadErrors, {
                [file.name]: data.responseText,
              }),
            });
          });
      },
      dragenter: (event) => {
        event.preventDefault(); // needed for IE
        // eslint-disable-next-line no-plusplus
        dragStatus++;

        // drag inside dropzone
        if (dragStatus === 1) {
          self.setState({ highlight: true });
        }
      },
      dragleave: () => {
        // eslint-disable-next-line no-plusplus
        dragStatus--;
        // drag outside dropzone
        if (dragStatus === 0) {
          self.setState({ highlight: false });
        }
      },
      previewTemplate:
        '<div class="dz-preview dz-file-preview">\n  <div class="dz-details">\n    <div class="dz-filename"><span data-dz-name notranslate></span></div>\n    <div class="dz-size" data-dz-size notranslate></div>\n    <img data-dz-thumbnail />\n  </div>\n  <div class="dz-progress"><span class="dz-upload" data-dz-uploadprogress></span></div>\n  <div class="dz-success-mark"><span>✔</span></div>\n  <div class="dz-error-mark"><span>✘</span></div>\n  <div class="dz-error-message"><span data-dz-errormessage notranslate></span></div>\n</div>',
    });
    window.blockDropzoneFilesizeTranslation();
  }

  /**
   * Action when opening the modal
   */
  open() {
    const itemImportID = window.merchantos.item_import.getItemImportIDFromURL();
    let expectedImageURL = '';

    this.setState({ itemImportID }, () => {
      expectedImageURL =
        `/API/Account/${window.merchantos.account.id}/ItemImport/` +
        `${this.state.itemImportID}/ExpectedItemImages.json`;
    });

    $.when(window.lsCloud.Config.access('cloudinary_upload_preset')).done(
      (cloudinaryUploadPreset) => {
        $.when(window.lsCloud.getJSONFromAPI(expectedImageURL)).done((data) => {
          // eslint-disable-next-line react/no-access-state-in-setstate
          const expectedFilenames = this.state.expectedFilenames;

          data.ExpectedItemImages.ExpectedImages.filename.forEach((val) => {
            expectedFilenames[this.decodeHtml(val)] = false;
          });

          data.ExpectedItemImages.UploadedImages.filename.forEach((val) => {
            expectedFilenames[this.decodeHtml(val)] = true;
          });

          this.setState({
            showModal: true,
            cloudinaryUploadPreset,
            expectedFilenames,
            remainingImages: data.ExpectedItemImages.ExpectedImages.filename.length,
            itemImportID: data.ExpectedItemImages.itemImportID,
          });
        });
      }
    );
  }

  /**
   * Action when closing the modal
   */
  close() {
    this.setState(initialState);

    $.when(window.merchantos.item_import.setExpectedImages()).done(() => {
      location.reload();
    });
  }

  decodeHtml(html) {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  }

  /**
   * Checks if percentage of total upload
   *
   * @private
   */
  checkUploadComplete() {
    const { uploadedImagePercentage } = this.state;
    this.setState({ uploadComplete: uploadedImagePercentage === '100%' });
  }

  /**
   * Checks if the image filename should be considered a match
   *
   * @param {String} filename
   * @private
   */
  checkImageMatch(filename) {
    const extensionRegex = /^(.+)(\.[^.]{1,4})$/;
    const query = [];
    const expectedFilenames = this.state.expectedFilenames;
    let ret = null;

    const match = filename.match(extensionRegex);

    // eslint-disable-next-line eqeqeq
    if (match != -1) {
      // Search for an exact match
      query.push(filename);

      // Special case for a repeated extension
      query.push(filename + match[2]);

      // eslint-disable-next-line eqeqeq
      if (match[2] == '.jpeg') {
        // Cloudinary replaces .jpeg with .jpg, so we need to search the source
        // array for the latter as well
        query.push(`${match[1]}.jpg`);
      }

      // Search for the root name
      query.push(match[1]);
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const val of query) {
      if (val in expectedFilenames) {
        ret = val;
      }
    }

    return ret;
  }

  /**
   * Updates total images uploaded and sets percentage for progress bar
   *
   * @private
   */
  updateUploadProgress() {
    this.setState(
      {
        // eslint-disable-next-line react/no-access-state-in-setstate
        totalImageCount: this.dropzone.getAcceptedFiles().length - this.state.imageOverLimitCount,
      },
      () => {
        this.setState({
          uploadedImagePercentage: `${
            // eslint-disable-next-line react/no-access-state-in-setstate
            (this.state.uploadedImageCount / this.state.totalImageCount) * 100
          }%`,
        });
      }
    );
  }

  /**
   * Increments number of skipped images and updates text to notify user
   *
   * @private
   */
  skipImage() {
    // eslint-disable-next-line react/no-access-state-in-setstate
    const skippedImages = this.state.skippedImages + 1;
    const plural = skippedImages > 1 ? 's' : '';
    const skippedImageMessage = `image${plural} failed`;

    this.setState({
      skippedImages,
      skippedImageMessage,
      highlight: false,
    });
  }

  toggleErrorScreen(event) {
    event.preventDefault();

    const { showErrors } = this.state;
    this.setState({
      showErrors: !showErrors,
      modalTitle: showErrors ? 'Upload Images' : 'Image Upload Errors',
    });
  }

  render() {
    const { showErrors } = this.state;

    return (
      <Modal
        className="import cirrus"
        isOpen={this.state.showModal}
        onRequestClose={this.close}
        onAfterOpen={this.onModalOpen}
      >
        <ModalHeader onCloseClicked={this.close}>
          <Group>
            {showErrors ? (
              <Button onClick={this.toggleErrorScreen}>
                <IconChevronLeft />
                Back
              </Button>
            ) : null}

            <div className={!this.state.showErrors ? 'upload-title' : ''}>
              {this.state.modalTitle}
            </div>
          </Group>
        </ModalHeader>
        <ModalBody>
          <div className="image-import-modal cr-position-relative" id="item-import">
            <div className="modal-container">
              <div
                className={`upload-container ${
                  this.state.showErrors ? 'hide-upload-container' : 'show-upload-container'
                }`}
              >
                <div className="cr-mt-2">
                  <div className="page-content">
                    <div
                      className={`dropzone-container center ${this.state.highlight && 'highlight'}`}
                      id="image-import-dropzone"
                    >
                      <div className="upload-csv">
                        <div className="upload-csv-title">
                          <span>Drag and drop Images or Folders</span>
                        </div>
                        <div className="upload-csv-text">
                          <span>OR</span>
                          <div className="cr-mt-1">
                            <button
                              type="button"
                              id="browse-images"
                              className="cr-button cr-button--secondary"
                            >
                              Browse...
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {this.state.skippedImages > 0 && (
                  <div className="skipped-image-container">
                    <a href="" onClick={this.toggleErrorScreen} className="skipped-images cr-pt-2">
                      <var>{this.state.skippedImages}</var> {this.state.skippedImageMessage}
                    </a>
                  </div>
                )}
                <div
                  className={`progress-bar -horizontal ${!this.state.showProgressBar && 'hidden'}`}
                  id="progressbar-horizontal-1"
                >
                  <div className="background">
                    <div style={{ width: this.state.uploadedImagePercentage }} className="bar" />
                  </div>
                  <div className="text">
                    Image upload in progress.{' '}
                    <a href="/?form_name=ui_tab&amp;tab=inventory" target="_blank">
                      Open a new tab to continue working.
                    </a>
                  </div>
                  <div className="progress-text">
                    <var>
                      {this.state.uploadedImageCount}/{this.state.totalImageCount}
                    </var>{' '}
                    images uploaded
                  </div>
                </div>
                <Divider className="cr-pv-3" />
                <div className="cr-ph-2 cr-text-s cr-gray-300">
                  <p>
                    Images can be of <strong>jpg, png</strong> or <strong>gif</strong> formats.
                  </p>
                  <p>
                    Images must be <strong>smaller than 8MB</strong>
                  </p>
                  <p>
                    Filenames may contain
                    <strong> up to 250 characters </strong>
                    and
                    <strong> must match </strong>
                    the name in the import file.
                  </p>
                  {this.state.imageCount > 0 && (
                    <div className="image-limit">
                      <br />
                      <span className="image-limit-text">
                        <var>{this.state.imageCount}</var> of
                        <var>{this.state.imageLimit}</var> images used
                      </span>{' '}
                      <a
                        href="https://retail-support.lightspeedhq.com/hc/en-us/articles/229130008#limit"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Learn More
                      </a>
                    </div>
                  )}
                </div>
              </div>
              <UploadErrors {...this.state} />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="cr-flex cr-justify-between">
            <a
              href="https://retail-support.lightspeedhq.com/hc/en-us/articles/115004838708"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="secondary">Help</Button>
            </a>
            <Group className="cr-justify-end">
              <Button onClick={this.close}>Cancel</Button>
              <Button
                variant="primary"
                fill
                onClick={this.close}
                disabled={!this.state.uploadComplete}
              >
                Done
              </Button>
            </Group>
          </div>
        </ModalFooter>
      </Modal>
    );
  }
}
